import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  searchEnquiries,
  exportEnquiries,
  setSearchParams,
  setSearchParamsFromURL,
  resetSearchParams,
  openModal,
  closeModal,
  clearErrors,
  urlParamsToSearchParams,
} from "../../features/enquiry/enquirySlice";
import "./enquiry.css";
import EnquiryModal from "../../components/EnquiryModal";
import NotFound from "../NotFound";
import { FaEye, FaFileExport, FaSpinner } from "react-icons/fa";

// A hand-tampered URL (e.g. ?pageNo=1fdasfadsf) should not silently load a
// broken dashboard. pageNo/pageSize must be plain positive integers when present.
const isValidQuery = (search) => {
  const params = new URLSearchParams(search);
  const intRegex = /^\d+$/;
  const pageNo = params.get("pageNo");
  const pageSize = params.get("pageSize");
  if (pageNo !== null && (!intRegex.test(pageNo) || parseInt(pageNo, 10) < 1)) {
    return false;
  }
  if (
    pageSize !== null &&
    (!intRegex.test(pageSize) || parseInt(pageSize, 10) < 1)
  ) {
    return false;
  }
  return true;
};

const Enquiry = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const {
    enquiries,
    searchLoading,
    searchError,
    searchParams,
    totalResults,
    isModalOpen,
    currentEnquiry,
    modalType,
    updateLoading,
    deleteLoading,
    exportLoading,
  } = useSelector((state) => state.enquiry);

  const [errors, setErrors] = useState({});
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const queryValid = isValidQuery(location.search);

  const interestedInOptions = [
    { value: "", label: "All Interests" },
    { value: "Podcast Recording", label: "Podcast Recording" },
    { value: "Cyclorama Wall", label: "Cyclorama Wall" },
    { value: "UGC / Content Creation", label: "UGC / Content Creation" },
    { value: "Commercial Shoot", label: "Commercial Shoot" },
    { value: "green screen zone", label: "Green Screen Zone" },
    { value: "Multi-Color Backdrop", label: "Multi-Color Backdrop" },
    { value: "other", label: "Other" },
  ];

  useEffect(() => {
    if (!queryValid) {
      setIsInitialLoad(false);
      return;
    }
    const loadFromURL = () => {
      const search = location.search;
      if (search) {
        const paramsFromURL = urlParamsToSearchParams(search);
        dispatch(setSearchParamsFromURL(paramsFromURL));
        dispatch(searchEnquiries(paramsFromURL));
      } else {
        dispatch(
          searchEnquiries({
            pageSize: "10",
            pageNo: "1",
          }),
        );
      }
      setIsInitialLoad(false);
    };

    loadFromURL();
  }, [dispatch, location, queryValid]);

  useEffect(() => {
    const handlePopState = () => {
      const search = window.location.search;
      if (!isValidQuery(search)) return;
      if (search) {
        const paramsFromURL = urlParamsToSearchParams(search);
        dispatch(setSearchParamsFromURL(paramsFromURL));
        dispatch(searchEnquiries(paramsFromURL));
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [dispatch]);

  useEffect(() => {
    if (searchError) {
      toast.error(searchError, {
        position: "top-right",
        autoClose: 3000,
      });
      dispatch(clearErrors());
    }
  }, [searchError, dispatch]);

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    dispatch(setSearchParams({ [name]: value }));
  };

  const validateDates = () => {
    const newErrors = {};

    if (searchParams.fromDate && !searchParams.toDate) {
      newErrors.date = "To date is required when From date is selected";
    }

    if (searchParams.toDate && !searchParams.fromDate) {
      newErrors.date = "From date is required when To date is selected";
    }

    if (searchParams.fromDate && searchParams.toDate) {
      const fromDate = new Date(searchParams.fromDate);
      const toDate = new Date(searchParams.toDate);

      if (fromDate > toDate) {
        newErrors.date = "From date cannot be greater than to date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!validateDates()) return;

    const params = { ...searchParams, pageNo: "1" };
    dispatch(setSearchParams({ pageNo: "1" }));
    dispatch(searchEnquiries(params));
  };

  const handleReset = () => {
    setErrors({});
    dispatch(resetSearchParams());
    window.history.pushState({}, "", window.location.pathname);
    dispatch(
      searchEnquiries({
        pageSize: "10",
        pageNo: "1",
      }),
    );
  };

  const handleExport = async () => {
    if (!validateDates()) {
      toast.error("Please fix date errors before exporting", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (totalResults === 0) {
      toast.warning("No data available to export", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      const exportParams = {};

      const filterKeys = [
        "name",
        "emailAdd",
        "phoneNo",
        "isBNIMember",
        "chapterName",
        "interestedIn",
        "fromDate",
        "toDate",
        "bookingDateFrom",
        "bookingDateTo",
        "createdDateFrom",
        "createdDateTo",
      ];

      filterKeys.forEach((key) => {
        if (searchParams[key] && searchParams[key].toString().trim() !== "") {
          exportParams[key] = searchParams[key];
        }
      });

      dispatch(exportEnquiries(exportParams));
    } catch (error) {
      toast.error("Failed to export: " + error.message, {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handlePagination = useCallback(
    (newPageNo) => {
      const params = { ...searchParams, pageNo: newPageNo.toString() };
      dispatch(setSearchParams({ pageNo: newPageNo.toString() }));
      dispatch(searchEnquiries(params));
    },
    [dispatch, searchParams],
  );

  const handleView = (enquiry) => {
    dispatch(openModal({ enquiry, type: "view" }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const totalPages = Math.ceil(totalResults / parseInt(searchParams.pageSize));

  if (!queryValid) {
    return <NotFound />;
  }

  if (isInitialLoad) {
    return <div className="text-center py-5">Loading...</div>;
  }

  return (
    <div className="enquiry-container">
      <div className="enquiry-header">
        <h2>Enquiry Management</h2>
        <p className="text-muted">Manage and track customer enquiries</p>
      </div>
      <div className="search-section card mb-4">
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">Search Filters</h5>
            {/* <small className="text-muted">Filters are saved in URL</small> */}
          </div>
          <small className="text-muted">
            Export All: {totalResults} records | Quick Export:{" "}
            {enquiries.length} records (current page)
          </small>
        </div>

        <div className="card-body">
          <form onSubmit={handleSearch}>
            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-control"
                  name="name"
                  value={searchParams.name}
                  onChange={handleSearchChange}
                  placeholder="Enter name"
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  name="emailAdd"
                  value={searchParams.emailAdd}
                  onChange={handleSearchChange}
                  placeholder="Enter email"
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Phone</label>
                <input
                  type="text"
                  className="form-control"
                  name="phoneNo"
                  value={searchParams.phoneNo}
                  onChange={handleSearchChange}
                  placeholder="Enter phone"
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Interested In</label>
                <select
                  className="form-control"
                  name="interestedIn"
                  value={searchParams.interestedIn}
                  onChange={handleSearchChange}
                >
                  {interestedInOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">From Date</label>
                <input
                  type="date"
                  className="form-control"
                  name="fromDate"
                  value={searchParams.fromDate}
                  onChange={handleSearchChange}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">To Date</label>
                <input
                  type="date"
                  className="form-control"
                  name="toDate"
                  value={searchParams.toDate}
                  onChange={handleSearchChange}
                />
              </div>

              <div className="col-md-2 mb-3">
                <label className="form-label">Page Size</label>
                <select
                  className="form-control"
                  name="pageSize"
                  value={searchParams.pageSize}
                  onChange={handleSearchChange}
                >
                  <option value="10">10 per page</option>
                  <option value="25">25 per page</option>
                  <option value="50">50 per page</option>
                  <option value="100">100 per page</option>
                </select>
              </div>
            </div>

            {errors.date && (
              <div className="alert alert-danger">{errors.date}</div>
            )}

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleReset}
                disabled={searchLoading || exportLoading}
              >
                Reset All
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={searchLoading || exportLoading}
              >
                {searchLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Searching...
                  </>
                ) : (
                  "Search"
                )}
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleExport}
                disabled={exportLoading || totalResults === 0}
                title="Export all filtered data"
              >
                {exportLoading ? (
                  <>
                    <FaSpinner className="spinner" />
                    <span className="ms-2">Exporting All...</span>
                  </>
                ) : (
                  <>
                    <FaFileExport className="me-2" />
                    Export All ({totalResults})
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="results-section card">
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Enquiries ({totalResults} found)</h5>
          <div>
            {searchLoading && (
              <div
                className="spinner-border spinner-border-sm text-primary me-2"
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
            )}
            {exportLoading && (
              <span className="badge bg-warning">
                <FaSpinner className="spinner" /> Exporting...
              </span>
            )}
          </div>
        </div>

        <div className="card-body">
          {enquiries.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">
                No enquiries found. Try adjusting your search filters.
              </p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Interested In</th>
                      <th>BNI Member</th>
                      <th>Booking Date</th>
                      <th>Created Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enquiries.map((enquiry) => (
                      <tr key={enquiry.pkEnquiryDetailId}>
                        <td>{enquiry.name}</td>
                        <td>{enquiry.emailAdd}</td>
                        <td>{enquiry.phoneNo}</td>
                        <td>
                          <div
                            className="d-inline-block"
                            style={{ maxWidth: "200px" }}
                          >
                            <div className="d-flex flex-wrap gap-1">
                              {enquiry.interestedIn
                                ?.split(",")
                                .slice(0, 2)
                                .map((interest, index) => (
                                  <span
                                    key={index}
                                    className="badge bg-info mb-1"
                                    style={{ fontSize: "0.75rem" }}
                                  >
                                    {interest.trim().length > 12
                                      ? `${interest.trim().substring(0, 12)}...`
                                      : interest.trim()}
                                  </span>
                                ))}

                              {enquiry.interestedIn?.split(",").length > 2 && (
                                <span
                                  className="badge bg-secondary"
                                  style={{ fontSize: "0.75rem" }}
                                  title={enquiry.interestedIn}
                                >
                                  +{enquiry.interestedIn.split(",").length - 2}{" "}
                                  more
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`badge ${enquiry.isBNIMember === "1"
                                ? "bg-success"
                                : "bg-secondary"
                              }`}
                          >
                            {enquiry.isBNIMember === "1" ? "Yes" : "No"}
                          </span>
                        </td>
                        <td>{formatDate(enquiry.bookingDate)}</td>
                        <td>{formatDate(enquiry.createdDate)}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleView(enquiry)}
                            title="View Details"
                          >
                            <FaEye />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-4">
                <div>
                  Showing{" "}
                  {(parseInt(searchParams.pageNo) - 1) *
                    parseInt(searchParams.pageSize) +
                    1}{" "}
                  to{" "}
                  {Math.min(
                    parseInt(searchParams.pageNo) *
                    parseInt(searchParams.pageSize),
                    totalResults,
                  )}{" "}
                  of {totalResults} enquiries
                </div>
                <nav>
                  <ul className="pagination mb-0">
                    <li
                      className={`page-item ${parseInt(searchParams.pageNo) === 1 ? "disabled" : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          handlePagination(parseInt(searchParams.pageNo) - 1)
                        }
                        disabled={
                          parseInt(searchParams.pageNo) === 1 || searchLoading
                        }
                      >
                        Previous
                      </button>
                    </li>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (parseInt(searchParams.pageNo) <= 3) {
                        pageNum = i + 1;
                      } else if (
                        parseInt(searchParams.pageNo) >=
                        totalPages - 2
                      ) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = parseInt(searchParams.pageNo) - 2 + i;
                      }

                      if (pageNum > 0 && pageNum <= totalPages) {
                        return (
                          <li
                            key={pageNum}
                            className={`page-item ${parseInt(searchParams.pageNo) === pageNum
                                ? "active"
                                : ""
                              }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePagination(pageNum)}
                              disabled={searchLoading}
                            >
                              {pageNum}
                            </button>
                          </li>
                        );
                      }
                      return null;
                    })}

                    <li
                      className={`page-item ${parseInt(searchParams.pageNo) === totalPages
                          ? "disabled"
                          : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          handlePagination(parseInt(searchParams.pageNo) + 1)
                        }
                        disabled={
                          parseInt(searchParams.pageNo) === totalPages ||
                          searchLoading
                        }
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </>
          )}
        </div>
      </div>

      {isModalOpen && (
        <EnquiryModal
          enquiry={currentEnquiry}
          type={modalType}
          onClose={() => dispatch(closeModal())}
          loading={updateLoading || deleteLoading}
        />
      )}
    </div>
  );
};

export default Enquiry;
